import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { withAdminAuthorization } from '../Session';
import * as ROUTES from '../../constants/routes';
import './FranchiseDetail.css';

class FranchiseDetailBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      franchise: null,
      owner: null,
      loading: true,
      showApprovalModal: false,
      showDisapproveModal: false,
      showDeleteModal: false,
      approving: false,
      disapproving: false,
      deleting: false,
      adminNotes: '',
      disapproveReason: '',
      error: null,
    };
  }

  componentDidMount() {
    const franchiseId = this.props.match.params.franchiseId;
    
    // Listen to both franchises and franchiseOnboarding collections
    this.unsubscribeFranchise = this.props.firebase.db
      .collection('franchises')
      .doc(franchiseId)
      .onSnapshot(doc => {
        if (doc.exists) {
          const franchiseData = { id: doc.id, ...doc.data() };
          
          // Load onboarding data which contains the detailed form data
          this.props.firebase.db
            .collection('franchiseOnboarding')
            .doc(franchiseId)
            .get()
            .then(onboardingDoc => {
              if (onboardingDoc.exists) {
                // Merge franchise data with onboarding data
                const onboardingData = onboardingDoc.data();
                franchiseData.personalDetails = onboardingData.personalDetails;
                franchiseData.teamMembers = onboardingData.teamMembers;
                franchiseData.territory = onboardingData.territory;
                franchiseData.brandingRequests = onboardingData.brandingRequests;
              }
              
              this.setState({ franchise: franchiseData }, () => {
                this.loadOwnerData(franchiseData.ownerId);
              });
            });
        } else {
          this.setState({ loading: false, error: 'Franchise not found' });
        }
      });
  }

  componentWillUnmount() {
    if (this.unsubscribeFranchise) {
      this.unsubscribeFranchise();
    }
  }

  loadOwnerData = async (ownerId) => {
    try {
      const ownerDoc = await this.props.firebase.franchiseAPI.franchiseOwner(ownerId).get();
      if (ownerDoc.exists) {
        this.setState({ owner: ownerDoc.data(), loading: false });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading owner:', error);
      this.setState({ loading: false });
    }
  };

  handleGoBack = () => {
    this.props.history.push(ROUTES.ADMIN_PANEL);
  };

  handleSignOut = () => {
    this.props.firebase.doSignOut();
    this.props.history.push(ROUTES.ADMIN_SIGNIN);
  };

  handleApprove = async () => {
    const { franchise, adminNotes } = this.state;
    this.setState({ approving: true, error: null });

    try {
      const timestamp = this.props.firebase.fieldValue.serverTimestamp();
      
      // Update franchise status to active
      await this.props.firebase.db
        .collection('franchises')
        .doc(franchise.id)
        .update({
          status: 'active',
          activatedAt: timestamp,
          adminNotes: adminNotes || null,
        });

      // Update franchise owner status to active
      await this.props.firebase.franchiseAPI
        .franchiseOwner(franchise.ownerId)
        .update({
          status: 'active',
          activatedAt: timestamp,
        });

      // Close modal and show success
      this.setState({
        showApprovalModal: false,
        approving: false,
        adminNotes: '',
      });

      alert('Franchise activated successfully! The owner can now access their dashboard.');
    } catch (error) {
      console.error('Error approving franchise:', error);
      this.setState({
        error: `Failed to approve franchise: ${error.message}`,
        approving: false,
      });
    }
  };

  handleDisapprove = async () => {
    const { franchise, disapproveReason } = this.state;
    this.setState({ disapproving: true, error: null });

    try {
      const timestamp = this.props.firebase.fieldValue.serverTimestamp();
      
      // Save to disapproved collection for admin records
      await this.props.firebase.db
        .collection('disapprovedFranchises')
        .doc(franchise.id)
        .set({
          ...franchise,
          disapproveReason: disapproveReason || 'No reason provided',
          disapprovedAt: timestamp,
          disapprovedBy: 'jeethbuildcon@gmail.com',
        });

      // Delete franchise onboarding data
      await this.props.firebase.db
        .collection('franchiseOnboarding')
        .doc(franchise.id)
        .delete();

      // Delete franchise
      await this.props.firebase.db
        .collection('franchises')
        .doc(franchise.id)
        .delete();

      // Delete franchise owner record
      await this.props.firebase.franchiseAPI
        .franchiseOwner(franchise.ownerId)
        .delete();

      alert('Franchise disapproved and archived. The user can start a new application.');
      
      // Redirect back to admin dashboard
      this.props.history.push(ROUTES.ADMIN_PANEL);
    } catch (error) {
      console.error('Error disapproving franchise:', error);
      this.setState({
        error: `Failed to disapprove franchise: ${error.message}`,
        disapproving: false,
      });
    }
  };

  handleDelete = async () => {
    const { franchise } = this.state;
    this.setState({ deleting: true, error: null });

    try {
      // Delete franchise onboarding data
      await this.props.firebase.db
        .collection('franchiseOnboarding')
        .doc(franchise.id)
        .delete();

      // Delete franchise
      await this.props.firebase.db
        .collection('franchises')
        .doc(franchise.id)
        .delete();

      // Delete franchise owner record
      await this.props.firebase.franchiseAPI
        .franchiseOwner(franchise.ownerId)
        .delete();

      alert('Franchise deleted successfully. The user can start a new application.');
      
      // Redirect back to admin dashboard
      this.props.history.push(ROUTES.ADMIN_PANEL);
    } catch (error) {
      console.error('Error deleting franchise:', error);
      this.setState({
        error: `Failed to delete franchise: ${error.message}`,
        deleting: false,
      });
    }
  };

  formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pending', color: '#ffc107', bg: '#fff3cd' },
      pending_setup: { label: 'Pending Setup', color: '#fd7e14', bg: '#ffe5d0' },
      active: { label: 'Active', color: '#28a745', bg: '#d4edda' },
    };
    const badge = badges[status] || { label: status, color: '#6c757d', bg: '#e9ecef' };
    return (
      <span
        className="status-badge-large"
        style={{
          background: badge.bg,
          color: badge.color,
          border: `2px solid ${badge.color}40`,
        }}
      >
        {badge.label}
      </span>
    );
  };

  render() {
    const { franchise, owner, loading, showApprovalModal, showDisapproveModal, showDeleteModal, approving, disapproving, deleting, adminNotes, disapproveReason, error } = this.state;

    if (loading) {
      return (
        <div className="admin-loading">
          <div className="spinner-large"></div>
          <p>Loading franchise details...</p>
        </div>
      );
    }

    if (error || !franchise) {
      return (
        <div className="admin-loading">
          <p style={{ color: '#c33' }}>{error || 'Franchise not found'}</p>
          <button onClick={this.handleGoBack} className="back-btn">Go Back</button>
        </div>
      );
    }

    const emailCosts = franchise.emailCosts || {};
    const totalEmails = emailCosts.totalEmails || 0;
    const additionalEmails = emailCosts.additionalEmails || 0;
    const totalCost = emailCosts.totalCost || 0;

    return (
      <div className="admin-dashboard">
        {/* Header */}
        <header className="admin-header">
          <div className="admin-header-content">
            <div className="admin-branding">
              <h1>Franchise Details</h1>
              <p>Douglas & McCartney</p>
            </div>
            <button onClick={this.handleSignOut} className="admin-signout-btn">
              <span>Sign Out</span>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="admin-main">
          <div className="detail-container">
            {/* Back Button and Status */}
            <div className="detail-header">
              <button onClick={this.handleGoBack} className="back-btn">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to Dashboard</span>
              </button>
              <div className="detail-status">
                {this.getStatusBadge(franchise.status)}
              </div>
            </div>

            {/* Personal Details */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <h2>Personal Details</h2>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <p>{franchise.personalDetails?.fullName || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{franchise.personalDetails?.email || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <p>{franchise.personalDetails?.phone || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Address</label>
                  <p>
                    {franchise.personalDetails?.address?.line1 || ''}{' '}
                    {franchise.personalDetails?.address?.line2 || ''}
                  </p>
                </div>
                <div className="detail-item">
                  <label>City</label>
                  <p>{franchise.personalDetails?.address?.city || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>State</label>
                  <p>{franchise.personalDetails?.address?.state || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Pincode</label>
                  <p>{franchise.personalDetails?.address?.pincode || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  <p>{franchise.personalDetails?.address?.country || 'India'}</p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h2>Team Members ({franchise.teamMembers?.length || 0})</h2>
              </div>
              {franchise.teamMembers && franchise.teamMembers.length > 0 ? (
                <div className="team-list">
                  {franchise.teamMembers.map((member, index) => (
                    <div key={index} className="team-member-card">
                      <div className="member-avatar">
                        {(member.name || 'U')[0].toUpperCase()}
                      </div>
                      <div className="member-info">
                        <h4>{member.name}</h4>
                        <p className="member-email">{member.email}</p>
                        <p className="member-role">{member.role || 'Team Member'}</p>
                        <p className="member-preferred">
                          <strong>Email to create:</strong> {member.preferredEmail}@douglasmccartney.com
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">No team members added</p>
              )}
            </div>

            {/* Email Costs */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h2>Email Account Costs</h2>
              </div>
              <div className="cost-breakdown">
                <div className="cost-item">
                  <span>Total Email Accounts</span>
                  <strong>{totalEmails}</strong>
                </div>
                <div className="cost-item">
                  <span>Free Email Included</span>
                  <strong>1</strong>
                </div>
                <div className="cost-item">
                  <span>Additional Emails</span>
                  <strong>{additionalEmails}</strong>
                </div>
                <div className="cost-item">
                  <span>Cost per Additional Email</span>
                  <strong>₹2,500</strong>
                </div>
                <div className="cost-item total-cost">
                  <span>Total Additional Cost</span>
                  <strong>₹{totalCost.toLocaleString('en-IN')}</strong>
                </div>
              </div>
            </div>

            {/* Territory & Coverage */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <h2>Territory & Coverage</h2>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Primary City</label>
                  <p>{franchise.territory?.primaryCity || 'N/A'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Coverage Areas</label>
                  <div className="tag-list">
                    {franchise.territory?.coverageAreas && franchise.territory.coverageAreas.length > 0 ? (
                      franchise.territory.coverageAreas.map((area, index) => (
                        <span key={index} className="tag">{area}</span>
                      ))
                    ) : (
                      <span>N/A</span>
                    )}
                  </div>
                </div>
                <div className="detail-item full-width">
                  <label>Property Types</label>
                  <div className="tag-list">
                    {franchise.territory?.propertyTypes && franchise.territory.propertyTypes.length > 0 ? (
                      franchise.territory.propertyTypes.map((type, index) => (
                        <span key={index} className="tag">{type}</span>
                      ))
                    ) : (
                      <span>N/A</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Branding Materials */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <h2>Branding Material Requests</h2>
              </div>
              <div className="branding-list">
                {franchise.brandingRequests && Object.keys(franchise.brandingRequests).length > 0 ? (
                  Object.entries(franchise.brandingRequests).map(([key, value]) => (
                    value && key !== 'notes' && (
                      <div key={key} className="branding-item">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                      </div>
                    )
                  ))
                ) : (
                  <p className="empty-message">No branding materials requested</p>
                )}
                {franchise.brandingRequests?.notes && (
                  <div className="branding-notes">
                    <label>Additional Notes:</label>
                    <p>{franchise.brandingRequests.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="detail-section">
              <div className="section-header">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2>Timeline</h2>
              </div>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <strong>Created</strong>
                    <p>{this.formatDate(franchise.createdAt)}</p>
                  </div>
                </div>
                {franchise.submittedAt && (
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                      <strong>Submitted</strong>
                      <p>{this.formatDate(franchise.submittedAt)}</p>
                    </div>
                  </div>
                )}
                {franchise.activatedAt && (
                  <div className="timeline-item">
                    <div className="timeline-marker active"></div>
                    <div className="timeline-content">
                      <strong>Activated</strong>
                      <p>{this.formatDate(franchise.activatedAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="detail-actions">
              {(franchise.status === 'pending' || franchise.status === 'pending_setup') && (
                <>
                  <button
                    onClick={() => this.setState({ showApprovalModal: true })}
                    className="approve-btn"
                  >
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Approve & Activate</span>
                  </button>
                  <button
                    onClick={() => this.setState({ showDisapproveModal: true })}
                    className="disapprove-btn"
                  >
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Disapprove</span>
                  </button>
                </>
              )}
              <button
                onClick={() => this.setState({ showDeleteModal: true })}
                className="delete-btn"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Delete Franchise</span>
              </button>
            </div>
          </div>
        </main>

        {/* Approval Modal */}
        {showApprovalModal && (
          <div className="modal-overlay" onClick={() => !approving && this.setState({ showApprovalModal: false })}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Approve & Activate Franchise</h3>
                <button
                  onClick={() => this.setState({ showApprovalModal: false })}
                  className="modal-close"
                  disabled={approving}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <p className="modal-description">
                  Before approving, please ensure you have completed the following tasks:
                </p>

                <div className="checklist">
                  <div className="checklist-item">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Created Google Workspace emails for owner + team ({totalEmails} total)</span>
                  </div>
                  <div className="checklist-item">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Prepared requested branding materials</span>
                  </div>
                  <div className="checklist-item">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Set up franchise portal access</span>
                  </div>
                  <div className="checklist-item">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Ready to send welcome email with credentials</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="adminNotes">Admin Notes (Optional)</label>
                  <textarea
                    id="adminNotes"
                    value={adminNotes}
                    onChange={(e) => this.setState({ adminNotes: e.target.value })}
                    placeholder="Add any internal notes about this approval..."
                    rows="4"
                    disabled={approving}
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => this.setState({ showApprovalModal: false })}
                  className="modal-btn cancel-btn"
                  disabled={approving}
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleApprove}
                  className="modal-btn approve-btn"
                  disabled={approving}
                >
                  {approving ? (
                    <>
                      <div className="spinner"></div>
                      <span>Activating...</span>
                    </>
                  ) : (
                    <>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Approve & Activate</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disapprove Modal */}
        {showDisapproveModal && (
          <div className="modal-overlay" onClick={() => !disapproving && this.setState({ showDisapproveModal: false })}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Disapprove Franchise Application</h3>
                <button
                  onClick={() => this.setState({ showDisapproveModal: false })}
                  className="modal-close"
                  disabled={disapproving}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <p className="modal-description" style={{ color: '#c33' }}>
                  <strong>Warning:</strong> Disapproving will permanently delete this franchise application and all related data. The user will be able to start a new application.
                </p>

                <div className="form-group">
                  <label htmlFor="disapproveReason">Reason for Disapproval (Optional)</label>
                  <textarea
                    id="disapproveReason"
                    value={disapproveReason}
                    onChange={(e) => this.setState({ disapproveReason: e.target.value })}
                    placeholder="Explain why this application is being disapproved..."
                    rows="4"
                    disabled={disapproving}
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => this.setState({ showDisapproveModal: false })}
                  className="modal-btn cancel-btn"
                  disabled={disapproving}
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleDisapprove}
                  className="modal-btn disapprove-btn"
                  disabled={disapproving}
                  style={{ background: '#dc3545' }}
                >
                  {disapproving ? (
                    <>
                      <div className="spinner"></div>
                      <span>Disapproving...</span>
                    </>
                  ) : (
                    <>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Confirm Disapprove</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => !deleting && this.setState({ showDeleteModal: false })}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Franchise</h3>
                <button
                  onClick={() => this.setState({ showDeleteModal: false })}
                  className="modal-close"
                  disabled={deleting}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <p className="modal-description" style={{ color: '#c33' }}>
                  <strong>Danger:</strong> This will permanently delete this franchise ({franchise.status}) and all related data. This action cannot be undone. The user will be able to start a new application.
                </p>

                <div style={{ padding: '16px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginTop: '16px' }}>
                  <p style={{ margin: 0, color: '#c33', fontSize: '14px' }}>
                    Are you absolutely sure you want to delete this franchise?
                  </p>
                </div>

                {error && (
                  <div className="error-message">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => this.setState({ showDeleteModal: false })}
                  className="modal-btn cancel-btn"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleDelete}
                  className="modal-btn delete-btn"
                  disabled={deleting}
                  style={{ background: '#c82333', color: 'white' }}
                >
                  {deleting ? (
                    <>
                      <div className="spinner"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Yes, Delete Permanently</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const FranchiseDetail = compose(
  withRouter,
  withFirebase,
  withAdminAuthorization,
)(FranchiseDetailBase);

export default FranchiseDetail;

